/* useCallWallet — Scan2Call per-minute billing math + fetch
   ----------------------------------------------------------
   Extracted from `DoorbellRingDialog.js` (Feb 2026). Owns:
     • Wallet balance fetch on connect + after every minute boundary
       (backend's per-minute watchdog debits NGN 30 each tick).
     • Funded-minutes derived state used by the funds banner
       (fundedMm:fundedSs left, next debit countdown, low-funds flag).

   Kept dumb: no UI. Caller decides how to render the numbers.
*/
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirror of backend's CALL_PRICE_PER_MIN_NGN. If you change this,
// update `backend/routes/doorbell.py` too.
export const CALL_PRICE_PER_MIN_NGN = 30;
// Below this many total funded seconds (incl. current minute), the
// banner flips to a low-funds warning style + nudges the resident.
export const LOW_FUNDS_THRESHOLD_S = 60;

/**
 * @param {{ enabled: boolean, elapsed: number }} opts
 *   enabled — true while the call is `connected`; false otherwise
 *   elapsed — seconds since the call connected (drives per-minute refetch)
 */
export function useCallWallet({ enabled, elapsed }) {
  // `null` while loading so the banner can hide until we have real data.
  const [walletBalance, setWalletBalance] = useState(null);

  const fetchWallet = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/doorbell/wallet`, { withCredentials: true });
      setWalletBalance(r.data?.balance_ngn ?? 0);
    } catch { /* noop — banner will stay hidden */ }
  }, []);

  // Initial fetch on connect.
  useEffect(() => {
    if (!enabled) return;
    fetchWallet();
  }, [enabled, fetchWallet]);

  // After every minute boundary, the backend watchdog has (or is about
  // to) debit another NGN 30. Re-pull the wallet so the banner reflects
  // the post-debit balance. Two reads (t+1.5s, t+3.5s) because the
  // watchdog tick can land a couple of seconds after our client-side
  // elapsed counter — the second read catches the debit if the first
  // raced ahead.
  useEffect(() => {
    if (!enabled) return undefined;
    if (elapsed === 0 || elapsed % 60 !== 0) return undefined;
    const t1 = setTimeout(fetchWallet, 1500);
    const t2 = setTimeout(fetchWallet, 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [enabled, elapsed, fetchWallet]);

  // Funded-minutes math. The CURRENT minute is already paid, so funded
  // talk-time is (full minutes in wallet * 60) + (seconds until the
  // next debit).
  const secsToNextDebit = enabled ? 60 - (elapsed % 60) : 0;
  const minutesFunded = walletBalance != null ? Math.floor(walletBalance / CALL_PRICE_PER_MIN_NGN) : null;
  // Clamp at 0 — a drained wallet mid-call briefly shows before the
  // backend watchdog force-ends; "-1:-31 left" is nonsense to users.
  const totalSecsLeft = minutesFunded != null ? Math.max(0, minutesFunded * 60 + secsToNextDebit) : null;
  const isLowFunds = totalSecsLeft != null && totalSecsLeft <= LOW_FUNDS_THRESHOLD_S;
  const fundedMm = totalSecsLeft != null ? Math.floor(totalSecsLeft / 60) : 0;
  const fundedSs = totalSecsLeft != null ? String(totalSecsLeft % 60).padStart(2, "0") : "00";
  const nextDebitSs = String(secsToNextDebit).padStart(2, "0");
  const hasWallet = walletBalance != null;

  return {
    walletBalance,
    hasWallet,
    isLowFunds,
    fundedMm,
    fundedSs,
    nextDebitSs,
    pricePerMinNgn: CALL_PRICE_PER_MIN_NGN,
  };
}
